import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { appConfirm } from "../../services/appDialogs";
import mammoth from "mammoth";
import {
  ArrowRight,
  AlertTriangle,
  BookOpen,
  Bot,
  Check,
  Clapperboard,
  Clock,
  FileText,
  Loader2,
  Minus,
  RefreshCw,
  Sparkles,
  Upload,
  Wand2,
  Workflow,
  WifiOff,
} from "lucide-react";
import { authenticatedFetch } from "../../utils/api";
import { API_HOST } from "../../utils/constants";
import { useTheme } from "../../context/ThemeContext";
import { useToast } from "../../context/ToastContext";
import { useStyle } from "../../context/StyleContext";
import {
  createTaskRequestId,
  cancelOutlineTask,
  findLatestTaskByExactSource,
  resumeTaskToCompletion,
  runTaskToCompletion,
} from "../../services/taskService";
import { outlineTaskAttachment, isOutlineSafetyStop, OUTLINE_STOP_CONFIRMATION } from "../../utils/outlineTaskSafety";
import {
  ACTIVE_ONBOARDING_STATUSES,
  shouldShowProjectOnboarding,
} from "./projectOnboardingState";

const ONBOARDING_STATE_KEY = "project_onboarding";

const STAGE_DEFS = [
  { key: "script", label: "导入剧本", desc: "保存项目总剧本" },
  { key: "split", label: "自动拆分", desc: "识别分集并写入单集剧本" },
  { key: "outline", label: "剧情大纲", desc: "生成项目剧情结构" },
  { key: "characters", label: "角色拆分", desc: "提取角色表" },
  { key: "scenes", label: "场景拆分", desc: "提取场景表" },
  { key: "props", label: "道具拆分", desc: "提取道具表" },
];

const initialStageState = () => STAGE_DEFS.reduce((acc, stage) => {
  acc[stage.key] = { status: "idle", message: "" };
  return acc;
}, {});

const FOUNDATION_TASKS = [
  { taskType: "OUTLINE_GENERATE", stageKey: "outline", source: "project_onboarding_outline" },
  { taskType: "OUTLINE_EXTRACT_CHARACTERS", stageKey: "characters", source: "project_onboarding_characters" },
  { taskType: "OUTLINE_EXTRACT_SCENES", stageKey: "scenes", source: "project_onboarding_scenes" },
  { taskType: "OUTLINE_EXTRACT_PROPS", stageKey: "props", source: "project_onboarding_props" },
];

const stageLabel = (key) => STAGE_DEFS.find((item) => item.key === key)?.label || key || "初始化";

const createOnboardingAbortError = () => {
  const error = new Error("项目初始化查询已取消");
  error.name = "AbortError";
  return error;
};

const isAbortError = (error) => error?.name === "AbortError";

const normalizeRestoredStages = (state) => {
  const restored = initialStageState();
  if (state?.stages && typeof state.stages === "object") {
    for (const definition of STAGE_DEFS) {
      if (state.stages[definition.key] && typeof state.stages[definition.key] === "object") {
        restored[definition.key] = { ...restored[definition.key], ...state.stages[definition.key] };
      }
    }
  }
  if (state?.scriptId) restored.script = { ...restored.script, status: "done", message: "已恢复保存的项目剧本" };
  if (state?.status === "split_preview") restored.split = { ...restored.split, status: "waiting", message: `已拆出 ${state.episodeCount || "若干"} 集，请确认后继续` };
  const currentStage = state?.failedStage || state?.currentStage;
  const foundationIndex = FOUNDATION_TASKS.findIndex((task) => task.stageKey === currentStage);
  if (foundationIndex >= 0) {
    restored.script = { ...restored.script, status: "done", message: "项目剧本已保存" };
    restored.split = { ...restored.split, status: "done", message: "分集剧本已写入" };
    for (let index = 0; index < foundationIndex; index += 1) {
      const priorStage = FOUNDATION_TASKS[index].stageKey;
      restored[priorStage] = { ...restored[priorStage], status: "done", message: "沿用上次成功结果", percent: 100 };
    }
  }
  if (currentStage && restored[currentStage]) {
    restored[currentStage] = {
      ...restored[currentStage],
      status: state.status === "import_failed" ? "failed" : "reconnecting",
      message: state.status === "import_failed" ? (state.error || "上次执行失败") : "页面已恢复，等待继续查询后台任务",
      taskId: state.currentTaskId || "",
      internalTaskId: state.internalTaskId || "",
      percent: Number.isFinite(Number(state.progress)) ? Number(state.progress) : 0,
    };
  }
  return restored;
};

const workspaceStateUrl = (projectId) => (
  `${API_HOST}/api/adaptation-agent/projects/${projectId}/workspace-state/${ONBOARDING_STATE_KEY}`
);

const readScriptFile = async (file) => {
  const name = file.name.toLowerCase();
  const title = file.name.replace(/\.[^.]+$/, "");
  let content;
  if (name.endsWith(".docx")) {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    content = result.value;
  } else {
    content = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target.result);
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }
  return { title, content };
};

const normalizeSplitPreview = (value) => {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
};

const ProjectOnboardingGate = ({ project, children }) => {
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [recoveryContext, setRecoveryContext] = useState(null);

  const refresh = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const [state, scripts, episodes] = await Promise.all([
        authenticatedFetch(workspaceStateUrl(project.id)).catch(() => ({})),
        authenticatedFetch(`${API_HOST}/api/script/list?projectId=${project.id}`).catch(() => null),
        authenticatedFetch(`${API_HOST}/api/project/episode/list?projectId=${project.id}`).catch(() => null),
      ]);

      setRecoveryContext({ state: state || {}, scripts: Array.isArray(scripts) ? scripts : [], episodes: Array.isArray(episodes) ? episodes : [] });
      setVisible(shouldShowProjectOnboarding({ state, scripts, episodes }));
    } finally {
      setLoading(false);
    }
  }, [project?.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center" style={{ backgroundColor: "var(--color-bg-primary)", color: "var(--color-text-muted)" }}>
        <Loader2 size={20} className="animate-spin" />
        <span className="ml-2 text-sm">加载项目...</span>
      </div>
    );
  }

  if (visible) {
    return (
      <ProjectOnboarding
        project={project}
        initialWorkspaceState={recoveryContext?.state}
        initialScripts={recoveryContext?.scripts}
        initialEpisodes={recoveryContext?.episodes}
        onDone={() => setVisible(false)}
      />
    );
  }

  return children;
};

const ProjectOnboarding = ({ project, initialWorkspaceState = {}, initialScripts = [], initialEpisodes = [], onDone }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { getTheme } = useTheme();
  const theme = getTheme();
  const fileInputRef = useRef(null);
  const autoResumeStartedRef = useRef(false);
  const mountedRef = useRef(false);
  const pollingControllersRef = useRef(new Map());
  const workspaceSaveQueueRef = useRef(Promise.resolve());

  const restoredStatus = String(initialWorkspaceState?.status || "");
  const [mode, setMode] = useState(() => ACTIVE_ONBOARDING_STATUSES.has(restoredStatus) ? "import" : "choice");
  const [modeTransitionPhase, setModeTransitionPhase] = useState("idle");
  const modeTransitionTimerRef = useRef(null);
  const modeTransitioningRef = useRef(false);
  const [selectedChoice, setSelectedChoice] = useState("script");
  const [scriptTitle, setScriptTitle] = useState("项目剧本");
  const [scriptText, setScriptText] = useState("");
  const [busy, setBusy] = useState(false);
  const [stopping, setStopping] = useState(false);
  const stopRequestedRef = useRef(false);
  const stopInFlightRef = useRef(false);
  const [stages, setStages] = useState(() => {
    const restored = normalizeRestoredStages(initialWorkspaceState);
    const failedStage = initialWorkspaceState?.failedStage || initialWorkspaceState?.currentStage;
    if (initialEpisodes.length > 0 && !["script", "split"].includes(failedStage)) {
      restored.split = { ...restored.split, status: "done", message: `已恢复 ${initialEpisodes.length} 集分集剧本` };
    }
    return restored;
  });
  const stagesRef = useRef(stages);
  stagesRef.current = stages;
  const [currentMessage, setCurrentMessage] = useState(() => initialWorkspaceState?.error || (restoredStatus === "in_progress" ? "检测到上次仍在执行的初始化任务" : ""));
  const [savedScriptId, setSavedScriptId] = useState(() => initialWorkspaceState?.scriptId || initialScripts?.[0]?.id || null);
  const [splitPreview, setSplitPreview] = useState(null);
  const [workflowError, setWorkflowError] = useState(() => {
    if (restoredStatus === "import_failed") {
      return {
        kind: isOutlineSafetyStop({ message: initialWorkspaceState?.error }) ? "safety_stopped" : "error",
        stage: initialWorkspaceState?.failedStage || initialWorkspaceState?.currentStage || "script",
        message: initialWorkspaceState?.error || "上次初始化失败，后台未返回详细错误",
        taskId: initialWorkspaceState?.currentTaskId || "",
        internalTaskId: initialWorkspaceState?.internalTaskId || "",
      };
    }
    if (restoredStatus === "in_progress") {
      return {
        kind: "reconnecting",
        stage: initialWorkspaceState?.currentStage || "outline",
        message: "页面已恢复。后台任务可能仍在执行，可继续查询，不会重新提交任务。",
        taskId: initialWorkspaceState?.currentTaskId || "",
        internalTaskId: initialWorkspaceState?.internalTaskId || "",
        requestId: initialWorkspaceState?.currentRequestId || initialWorkspaceState?.internalTaskId || "",
        taskType: initialWorkspaceState?.currentTaskType || "",
        source: initialWorkspaceState?.currentSource || "",
      };
    }
    return null;
  });
  const activeStageRef = useRef(initialWorkspaceState?.failedStage || initialWorkspaceState?.currentStage || "");

  const provider = useMemo(() => localStorage.getItem("ai_provider") || "omini", []);
  const { currentStyle } = useStyle();
  const hasScriptText = scriptText.trim().length > 0;
  const splitEpisodes = Array.isArray(splitPreview?.episodes) ? splitPreview.episodes : [];
  // 跳过剧情大纲：只拆角色/场景/道具（大纲对资产拆分非必需，提取直接读原剧本）
  const [skipOutline, setSkipOutline] = useState(false);

  const transitionToMode = useCallback((nextMode, { immediate = false } = {}) => {
    if (!nextMode || nextMode === mode || modeTransitioningRef.current) return;
    window.clearTimeout(modeTransitionTimerRef.current);
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced || immediate) {
      setMode(nextMode);
      setModeTransitionPhase("enter");
      return;
    }
    modeTransitioningRef.current = true;
    setModeTransitionPhase("exit");
    modeTransitionTimerRef.current = window.setTimeout(() => {
      modeTransitioningRef.current = false;
      setMode(nextMode);
      setModeTransitionPhase("enter");
    }, 140);
  }, [mode]);

  const saveWorkspaceState = useCallback((status, extra = {}) => {
    // 串行化状态写入：防止较早的 in_progress 慢响应覆盖后面的 completed/import_failed。
    const operation = workspaceSaveQueueRef.current
      .catch(() => undefined)
      .then(() => authenticatedFetch(workspaceStateUrl(project.id), {
        method: "POST",
        body: JSON.stringify({
          status,
          updatedAt: new Date().toISOString(),
          ...extra,
        }),
      }));
    workspaceSaveQueueRef.current = operation;
    return operation;
  }, [project.id]);

  useEffect(() => {
    const controllers = pollingControllersRef.current;
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      autoResumeStartedRef.current = false;
      for (const controller of controllers.values()) controller.abort();
      controllers.clear();
      window.clearTimeout(modeTransitionTimerRef.current);
      modeTransitioningRef.current = false;
    };
  }, []);

  const setStage = useCallback((key, status, message = "", extra = {}) => {
    activeStageRef.current = key;
    setStages((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), status, message, ...extra },
    }));
    if (message) setCurrentMessage(message);
  }, []);

  useEffect(() => {
    const scriptId = initialWorkspaceState?.scriptId || initialScripts?.[0]?.id;
    if (!scriptId || !ACTIVE_ONBOARDING_STATUSES.has(String(initialWorkspaceState?.status || ""))) return;
    let cancelled = false;

    const restoreSavedScript = async () => {
      try {
        const script = await authenticatedFetch(`${API_HOST}/api/script/${scriptId}`);
        if (cancelled || !script) return;
        setSavedScriptId(script.id || scriptId);
        setScriptTitle(script.title || initialScripts?.[0]?.title || "项目剧本");
        setScriptText(script.content || script.scriptContent || "");

        const shouldRestorePreview = initialWorkspaceState?.status === "split_preview"
          || (initialWorkspaceState?.status === "import_failed" && (initialWorkspaceState?.failedStage || initialWorkspaceState?.currentStage) === "split");
        if (shouldRestorePreview) {
          try {
            const preview = normalizeSplitPreview(await authenticatedFetch(`${API_HOST}/api/script/${scriptId}/split-preview`));
            if (!cancelled && Array.isArray(preview?.episodes) && preview.episodes.length > 0) {
              setSplitPreview(preview);
              setStage("split", initialWorkspaceState?.status === "split_preview" ? "waiting" : "failed", initialWorkspaceState?.status === "split_preview"
                ? `已恢复 ${preview.episodes.length} 集拆分结果，请确认后继续`
                : (initialWorkspaceState?.error || "上次拆分失败，可重新确认"));
            }
          } catch (previewError) {
            if (!cancelled) setCurrentMessage(`已恢复剧本，但拆分预览恢复失败：${previewError.message}`);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setWorkflowError((previous) => previous || {
            kind: "error",
            stage: "script",
            message: `恢复已保存剧本失败：${error.message}`,
            taskId: "",
          });
        }
      }
    };

    void restoreSavedScript();
    return () => { cancelled = true; };
  }, [initialScripts, initialWorkspaceState, setStage]);

  const clearSplitConfirmation = useCallback((message = "") => {
    setSplitPreview(null);
    setStages((prev) => ({
      ...prev,
      split: { status: message ? "idle" : prev.split?.status || "idle", message },
    }));
    if (message) setCurrentMessage(message);
  }, []);

  const handleFileChange = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const result = await readScriptFile(file);
      if (!result.content?.trim()) {
        toast.error("文件内容为空");
        return;
      }
      setScriptTitle(result.title || "项目剧本");
      setScriptText(result.content);
      clearSplitConfirmation("剧本内容已更新，请重新拆分确认");
      toast.success(`已读取：${file.name}`);
    } catch (error) {
      setWorkflowError({ kind: "error", stage: "script", message: `读取文件失败：${error.message}`, taskId: "" });
      setStage("script", "failed", `读取文件失败：${error.message}`);
      toast.error("读取失败: " + error.message);
    } finally {
      event.target.value = "";
    }
  }, [clearSplitConfirmation, setStage, toast]);

  const handleScriptTextChange = useCallback((event) => {
    setScriptText(event.target.value);
    if (savedScriptId || splitPreview) {
      clearSplitConfirmation("剧本内容已修改，请重新拆分确认");
    }
  }, [clearSplitConfirmation, savedScriptId, splitPreview]);

  const runOutlineTask = useCallback(async (taskType, stageKey, source, script, resume = {}) => {
    pollingControllersRef.current.get(stageKey)?.abort();
    const controller = new AbortController();
    pollingControllersRef.current.set(stageKey, controller);
    const assertActive = () => {
      if (!mountedRef.current || controller.signal.aborted) throw createOnboardingAbortError();
    };
    const isResume = Boolean(resume.taskId);
    let requestId = resume.requestId
      || resume.internalTaskId
      || (isResume ? resume.taskId : "")
      || createTaskRequestId(`onboarding-${stageKey}`);
    const description = STAGE_DEFS.find((item) => item.key === stageKey)?.desc || "处理中";
    assertActive();
    setWorkflowError(null);
    setStage(stageKey, "running", isResume ? "正在重新查询后台任务" : description, {
      percent: 0,
      taskId: resume.taskId || requestId,
      internalTaskId: resume.internalTaskId || requestId,
    });

    const pollingOptions = {
      projectId: Number(project.id),
      provider,
      styleAnchor: currentStyle,
      source,
      requestId,
      taskType,
      internalTaskId: resume.internalTaskId || resume.taskId,
      timeoutMs: 30 * 60 * 1000,
      signal: controller.signal,
      recoverSubmittedTask: () => findLatestTaskByExactSource({
        projectId: Number(project.id),
        source,
        requestId,
        signal: controller.signal,
      }),
      onSubmissionRecovered: (task) => {
        assertActive();
        setStage(stageKey, "running", "提交响应丢失，已找回原任务并继续查询", {
          percent: 0,
          taskId: task.taskId,
          internalTaskId: task.internalTaskId || requestId,
        });
      },
      onSubmitted: async (task) => {
        assertActive();
        const attachment = outlineTaskAttachment(task, requestId, source);
        requestId = attachment.requestId;
        if (attachment.deduplicated) toast.warning(attachment.message);
        setStage(stageKey, "running", attachment.message, {
          percent: 0,
          taskId: task.taskId,
          internalTaskId: task.internalTaskId,
          provider: attachment.provider || provider,
          attachmentMessage: attachment.deduplicated ? attachment.message : "",
        });
        try {
          // 任务 ID 是刷新恢复的关键信息，taskService 会 await 此回调后才开始轮询。
          await saveWorkspaceState("in_progress", {
            scriptId: savedScriptId,
            currentStage: stageKey,
            currentTaskId: task.taskId,
            internalTaskId: task.internalTaskId || requestId,
            currentRequestId: requestId,
            currentTaskType: taskType,
            currentSource: source,
            canonicalSource: attachment.canonicalSource,
            currentProvider: attachment.provider || provider,
            progress: 0,
          });
          assertActive();
        } catch (stateError) {
          assertActive();
          setWorkflowError({
            kind: "state_save_warning",
            stage: stageKey,
            message: `任务已提交，但任务 ID 同步失败：${stateError.message}。本页会继续查询原任务。`,
            taskId: task.taskId,
            internalTaskId: task.internalTaskId || requestId,
            requestId,
            taskType,
            source,
          });
        }
      },
      queryDetailedProgress: async (task, transportStatus) => {
        const transport = String(transportStatus?.status || "").toLowerCase();
        if (["success", "failed", "canceled", "not_found"].includes(transport)) return null;
        const internalTaskId = task?.internalTaskId || task?.taskId;
        if (!internalTaskId) return null;
        const detail = await authenticatedFetch(`${API_HOST}/api/outline/progress/${encodeURIComponent(internalTaskId)}`);
        const detailStage = String(detail?.stage || "").toLowerCase();
        const subStep = String(detail?.subStep || "").toLowerCase();
        return {
          status: detailStage === "error" ? "failed" : detailStage === "done" ? "persisting" : subStep || "processing",
          currentStage: subStep || detailStage || "processing",
          currentStageText: [detail?.stageLabel, detail?.subStepLabel].filter(Boolean).join(" · ") || "后台任务执行中",
          progress: detail?.percent,
          totalSegments: detail?.totalSegments,
          completedSegments: detail?.completedSegments,
          errorMessage: detail?.error || "",
        };
      },
      onProgress: (status) => {
        if (!mountedRef.current || controller.signal.aborted) return;
        const transport = String(status.transportStatus || status.status || "processing").toLowerCase();
        const failed = ["failed", "error", "not_found", "canceled"].includes(transport);
        const done = ["success", "done"].includes(transport);
        const nextPercent = Number(status.progress ?? status.percent);
        setStage(stageKey, failed ? "failed" : done ? "done" : "running", status.currentStageText || status.status || "处理中", {
          percent: Number.isFinite(nextPercent) ? Math.max(0, Math.min(100, nextPercent)) : 0,
          totalSegments: Number(status.totalSegments || 0),
          completedSegments: Number(status.completedSegments || 0),
          taskId: status.taskId || resume.taskId || "",
          internalTaskId: status.internalTaskId || resume.internalTaskId || "",
          currentStage: status.currentStage || status.status || "processing",
        });
      },
      onDetailedProgressError: ({ taskId, internalTaskId }) => {
        if (!mountedRef.current || controller.signal.aborted) return;
        setStage(stageKey, "running", "后台任务仍在执行，详细进度暂时不可用", { taskId, internalTaskId });
      },
      onPollError: ({ consecutiveErrors, error, taskId, internalTaskId }) => {
        if (!mountedRef.current || controller.signal.aborted) return;
        const message = `后台任务仍在执行，正在重新连接（第 ${consecutiveErrors} 次）`;
        setStage(stageKey, "reconnecting", message, {
          taskId,
          internalTaskId,
          reconnectMessage: error?.message || "任务状态查询暂时不可用",
        });
        setWorkflowError({
          kind: "reconnecting",
          stage: stageKey,
          message: `${message}。不会重复提交任务。`,
          taskId,
          internalTaskId,
          taskType,
          source,
          requestId,
        });
        void saveWorkspaceState("in_progress", {
          scriptId: savedScriptId,
          currentStage: stageKey,
          currentTaskId: taskId,
          internalTaskId,
          currentRequestId: requestId,
          currentTaskType: taskType,
          currentSource: source,
          reconnectError: error?.message || "",
        }).catch(() => {});
      },
    };

    try {
      if (!isResume) {
        // POST 之前先持久化 requestId。持久化 Worker 以它作为幂等键，即使 POST 响应丢失也不会重复生成。
        await saveWorkspaceState("in_progress", {
          scriptId: savedScriptId,
          currentStage: stageKey,
          currentTaskId: requestId,
          internalTaskId: requestId,
          currentRequestId: requestId,
          currentTaskType: taskType,
          currentSource: source,
          progress: 0,
        });
        assertActive();
      }
      const completed = isResume
        ? await resumeTaskToCompletion(resume.taskId, pollingOptions)
        : await runTaskToCompletion(
            taskType,
            { projectId: Number(project.id), script, provider, styleAnchor: currentStyle },
            pollingOptions
          );
      assertActive();
      setStage(stageKey, "done", "完成", {
        percent: 100,
        taskId: completed.taskId,
        internalTaskId: completed.internalTaskId,
      });
      setWorkflowError(null);
      return completed;
    } catch (error) {
      if (isAbortError(error) || controller.signal.aborted || !mountedRef.current) {
        throw createOnboardingAbortError();
      }
      const failedTaskId = error?.taskId || resume.taskId || requestId;
      const failedInternalTaskId = error?.internalTaskId || resume.internalTaskId || requestId;
      if (error?.pollingInterrupted) {
        setStage(stageKey, "reconnecting", "状态查询已暂停，后台任务可能仍在执行", {
          taskId: failedTaskId,
          internalTaskId: failedInternalTaskId,
          reconnectMessage: error.message || "网络连接持续异常",
        });
        setWorkflowError({
          kind: "reconnecting",
          stage: stageKey,
          message: "状态查询已暂停，后台任务不会被重复提交；网络恢复后可继续查询。",
          taskId: failedTaskId,
          internalTaskId: failedInternalTaskId,
          taskType,
          source,
          requestId,
        });
        await saveWorkspaceState("in_progress", {
          scriptId: savedScriptId,
          currentStage: stageKey,
          currentTaskId: failedTaskId,
          internalTaskId: failedInternalTaskId,
          currentRequestId: requestId,
          currentTaskType: taskType,
          currentSource: source,
          reconnectError: error.message || "网络连接持续异常",
        }).catch(() => {});
        error.failedStage = stageKey;
        error.requestId = requestId;
        error.taskType = taskType;
        error.source = source;
        throw error;
      }
      setStage(stageKey, "failed", error.message || "任务失败", {
        taskId: failedTaskId,
        internalTaskId: failedInternalTaskId,
      });
      setWorkflowError({
        kind: isOutlineSafetyStop(error) ? "safety_stopped" : "error",
        stage: stageKey,
        message: error.message || "后台未返回详细错误",
        taskId: failedTaskId,
        internalTaskId: failedInternalTaskId,
        requestId,
        taskType,
        source,
      });
      await saveWorkspaceState("import_failed", {
        scriptId: savedScriptId,
        failedStage: stageKey,
        currentTaskId: failedTaskId,
        internalTaskId: failedInternalTaskId,
        currentRequestId: requestId,
        currentTaskType: taskType,
        currentSource: source,
        error: error.message || "后台未返回详细错误",
        failedAt: new Date().toISOString(),
      }).catch(() => {});
      error.failedStage = stageKey;
      error.requestId = requestId;
      error.taskType = taskType;
      error.source = source;
      throw error;
    } finally {
      if (pollingControllersRef.current.get(stageKey) === controller) {
        pollingControllersRef.current.delete(stageKey);
      }
    }
  }, [currentStyle, project.id, provider, saveWorkspaceState, savedScriptId, setStage, toast]);

  const stopFoundationTask = useCallback(async () => {
    if (stopInFlightRef.current) return;
    stopInFlightRef.current = true;
    setStopping(true);
    try {
      if (!(await appConfirm(OUTLINE_STOP_CONFIRMATION))) return;
      // Even if the current request finishes during cancellation, do not launch the next stage.
      stopRequestedRef.current = true;
      // A stage can finish while the confirmation is open. Stop the current stage, not the old one.
      const stageKey = activeStageRef.current;
      const stage = stagesRef.current[stageKey];
      const taskId = stage?.internalTaskId || stage?.taskId;
      if (!FOUNDATION_TASKS.some((task) => task.stageKey === stageKey) || !taskId) return;
      await cancelOutlineTask(taskId);
      const stoppedMessage = "用户已停止本批初始化；后续阶段不会自动提交，已发出的请求仍可能计费。";
      setWorkflowError({
        kind: "safety_stopped", stage: stageKey, message: stoppedMessage,
        taskId: stage.taskId || taskId, internalTaskId: taskId,
      });
      await saveWorkspaceState("import_failed", {
        scriptId: savedScriptId, failedStage: stageKey,
        currentTaskId: stage.taskId || taskId, internalTaskId: taskId,
        currentRequestId: taskId, error: stoppedMessage,
        failedAt: new Date().toISOString(),
      });
      toast.warning("停止请求已提交；后续初始化阶段不会继续发送，已发出的请求仍可能计费");
      if (!pollingControllersRef.current.has(stageKey)) {
        const task = FOUNDATION_TASKS.find((item) => item.stageKey === stageKey);
        await runOutlineTask(task.taskType, stageKey, task.source, scriptText, {
          taskId: stage.taskId || taskId, internalTaskId: taskId, requestId: taskId,
        });
      }
    } catch (error) {
      if (!isOutlineSafetyStop(error)) toast.error(`停止结果未能确认，请查询原任务：${error.message}`);
    } finally {
      stopInFlightRef.current = false;
      if (mountedRef.current) setStopping(false);
    }
  }, [runOutlineTask, saveWorkspaceState, savedScriptId, scriptText, toast]);

  const runFoundationTasks = useCallback(async (scriptId, options = {}) => {
    if (!mountedRef.current) throw createOnboardingAbortError();
    // 跳过大纲模式：只跑角色/场景/道具三个资产拆分任务
    const tasks = skipOutline
      ? FOUNDATION_TASKS.filter((item) => item.stageKey !== "outline")
      : FOUNDATION_TASKS;
    if (skipOutline) {
      setStage("outline", "skipped", "已跳过（只拆角色/场景/道具）");
    }
    const requestedStage = options.startAtStage;
    const requestedIndex = tasks.findIndex((item) => item.stageKey === requestedStage);
    const startIndex = requestedIndex >= 0 ? requestedIndex : 0;
    for (let index = 0; index < startIndex; index += 1) {
      setStage(tasks[index].stageKey, "done", "沿用上次成功结果", { percent: 100 });
    }

    for (let index = startIndex; index < tasks.length; index += 1) {
      if (!mountedRef.current) throw createOnboardingAbortError();
      if (stopRequestedRef.current) throw createOnboardingAbortError();
      const task = tasks[index];
      const resume = index === startIndex ? (options.resume || {}) : {};
      try {
        await runOutlineTask(task.taskType, task.stageKey, task.source, scriptText, resume);
        if (!mountedRef.current) throw createOnboardingAbortError();
      } catch (error) {
        if (isAbortError(error) || !mountedRef.current) throw createOnboardingAbortError();
        if (error?.pollingInterrupted) throw error;
        // 任一基础资产失败都立即停在该阶段。继续后续阶段会让 workspace
        // 被新的 in_progress 覆盖，刷新后就会把真实失败误判成“已成功”。
        setStage(task.stageKey, "failed", error.message || "未完成", {
          taskId: error.taskId || resume.taskId || "",
          internalTaskId: error.internalTaskId || resume.internalTaskId || "",
        });
        throw error;
      }
    }

    if (!mountedRef.current || stopRequestedRef.current) throw createOnboardingAbortError();
    try {
      await saveWorkspaceState("completed", {
        scriptId,
        completedAt: new Date().toISOString(),
        warnings: [],
      });
    } catch (stateError) {
      if (!mountedRef.current) throw createOnboardingAbortError();
      // AI 四个阶段均已成功；只标记“完成状态保存失败”，绝不把 props 改成失败或再次生成。
      setWorkflowError({
        kind: "completion_save_error",
        stage: "completion",
        message: `剧情大纲、角色、场景和道具均已完成，但流程完成状态保存失败：${stateError.message}`,
        taskId: "",
        internalTaskId: "",
      });
      setCurrentMessage("所有 AI 拆分已完成，仅需重新保存流程状态");
      toast.warning("所有 AI 拆分已完成，但完成状态保存失败；不会重复生成。");
      return { completed: false, warnings: [], completionSaveError: true };
    }
    if (!mountedRef.current) throw createOnboardingAbortError();
    setWorkflowError(null);
    toast.success("项目地基已生成");
    navigate(`/project/${project.id}/script-analysis`);
    return { completed: true, warnings: [] };
  }, [navigate, project.id, runOutlineTask, saveWorkspaceState, scriptText, setStage, skipOutline, toast]);

  const startScriptBootstrap = useCallback(async () => {
    if (!hasScriptText || busy) return;
    stopRequestedRef.current = false;
    const existingScriptId = savedScriptId;
    setBusy(true);
    setStages(initialStageState());
    setSavedScriptId(existingScriptId || null);
    setSplitPreview(null);
    setWorkflowError(null);
    setCurrentMessage("准备导入剧本");

    let nextScriptId = existingScriptId;
    try {
      setStage("script", "running", "正在保存总剧本");
      await saveWorkspaceState("in_progress", {
        scriptId: nextScriptId,
        currentStage: "script",
        progress: 0,
      });
      if (!mountedRef.current) throw createOnboardingAbortError();
      if (nextScriptId) {
        await authenticatedFetch(`${API_HOST}/api/script/${nextScriptId}`, {
          method: "PUT",
          body: JSON.stringify({
            title: scriptTitle.trim() || "项目剧本",
            content: scriptText,
          }),
        });
      } else {
        const script = await authenticatedFetch(`${API_HOST}/api/script/create`, {
          method: "POST",
          body: JSON.stringify({
            projectId: Number(project.id),
            title: scriptTitle.trim() || "项目剧本",
            content: scriptText,
          }),
        });
        nextScriptId = script.id;
      }
      if (!mountedRef.current) throw createOnboardingAbortError();
      setSavedScriptId(nextScriptId);
      setStage("script", "done", "剧本已保存");

      setStage("split", "running", "正在识别分集标记");
      await saveWorkspaceState("in_progress", {
        scriptId: nextScriptId,
        currentStage: "split",
        progress: 0,
      });
      if (!mountedRef.current) throw createOnboardingAbortError();
      const preview = await authenticatedFetch(`${API_HOST}/api/script/${nextScriptId}/split-preview`);
      if (!mountedRef.current) throw createOnboardingAbortError();
      const nextPreview = normalizeSplitPreview(preview);

      if (!(nextPreview?.method === "rule") || !Array.isArray(nextPreview?.episodes) || nextPreview.episodes.length < 1) {
        throw new Error("剧本内容为空或无法拆分，请检查后重试");
      }

      setSplitPreview(nextPreview);
      setStage("split", "waiting", `已拆出 ${nextPreview.episodes.length} 集，请确认后继续`);
      await saveWorkspaceState("split_preview", {
        scriptId: nextScriptId,
        method: nextPreview.method,
        episodeCount: nextPreview.episodes.length,
        previewedAt: new Date().toISOString(),
      });
      if (!mountedRef.current) throw createOnboardingAbortError();
      toast.info(`已拆出 ${nextPreview.episodes.length} 集，请确认集数`);
    } catch (error) {
      if (isAbortError(error) || !mountedRef.current) return;
      const failedStage = activeStageRef.current || "script";
      setStage(failedStage, "failed", error.message || "初始化失败");
      setWorkflowError({ kind: "error", stage: failedStage, message: error.message || "初始化失败", taskId: "" });
      toast.error("初始化失败: " + error.message);
      await saveWorkspaceState("import_failed", {
        scriptId: nextScriptId,
        failedStage,
        error: error.message,
        failedAt: new Date().toISOString(),
      }).catch(() => {});
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  }, [busy, hasScriptText, project.id, savedScriptId, saveWorkspaceState, scriptText, scriptTitle, setStage, toast]);

  const confirmSplitAndContinue = useCallback(async () => {
    if (!savedScriptId || !splitPreview || splitEpisodes.length < 1 || busy) return;
    stopRequestedRef.current = false;
    setBusy(true);
    setWorkflowError(null);
    try {
      const mode = "rule";
      setStage("split", "running", `正在写入 ${splitEpisodes.length} 集`);
      await saveWorkspaceState("in_progress", {
        scriptId: savedScriptId,
        currentStage: "split",
        progress: 0,
      });
      if (!mountedRef.current) throw createOnboardingAbortError();
      await authenticatedFetch(`${API_HOST}/api/script/${savedScriptId}/confirm-split`, {
        method: "POST",
        body: JSON.stringify({ projectId: Number(project.id), mode }),
      });
      if (!mountedRef.current) throw createOnboardingAbortError();
      setStage("split", "done", `已确认 ${splitEpisodes.length} 集`);
      setSplitPreview(null);
      await runFoundationTasks(savedScriptId);
    } catch (error) {
      if (isAbortError(error) || !mountedRef.current) return;
      const failedStage = error.failedStage || activeStageRef.current || "split";
      if (error?.pollingInterrupted) {
        setStage(failedStage, "reconnecting", "状态查询已暂停，后台任务可能仍在执行", {
          taskId: error.taskId || "",
          internalTaskId: error.internalTaskId || "",
          reconnectMessage: error.message || "网络连接持续异常",
        });
        setWorkflowError((previous) => previous?.kind === "reconnecting" ? previous : {
          kind: "reconnecting",
          stage: failedStage,
          message: "状态查询已暂停，后台任务不会被重复提交；网络恢复后可继续查询。",
          taskId: error.taskId || "",
          internalTaskId: error.internalTaskId || "",
          requestId: error.requestId || error.internalTaskId || "",
          taskType: error.taskType || "",
          source: error.source || "",
        });
        toast.warning("状态查询已暂停，可稍后继续查询原任务");
        await saveWorkspaceState("in_progress", {
          scriptId: savedScriptId,
          currentStage: failedStage,
          currentTaskId: error.taskId || "",
          internalTaskId: error.internalTaskId || "",
          currentRequestId: error.requestId || error.internalTaskId || "",
          currentTaskType: error.taskType || "",
          currentSource: error.source || "",
          reconnectError: error.message || "网络连接持续异常",
        }).catch(() => {});
        return;
      }
      setStage(failedStage, "failed", error.message || "继续初始化失败", {
        taskId: error.taskId || "",
        internalTaskId: error.internalTaskId || "",
        requestId: error.requestId || error.internalTaskId || "",
        taskType: error.taskType || "",
        source: error.source || "",
      });
      setWorkflowError((previous) => previous?.stage === failedStage ? previous : {
        kind: "error",
        stage: failedStage,
        message: error.message || "继续初始化失败",
        taskId: error.taskId || "",
        internalTaskId: error.internalTaskId || "",
      });
      toast.error("继续初始化失败: " + error.message);
      await saveWorkspaceState("import_failed", {
        scriptId: savedScriptId,
        failedStage,
        currentTaskId: error.taskId || "",
        internalTaskId: error.internalTaskId || "",
        currentRequestId: error.requestId || error.internalTaskId || "",
        currentTaskType: error.taskType || "",
        currentSource: error.source || "",
        error: error.message,
        failedAt: new Date().toISOString(),
      }).catch(() => {});
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  }, [busy, project.id, runFoundationTasks, saveWorkspaceState, savedScriptId, setStage, splitEpisodes.length, splitPreview, toast]);

  const retryWorkflow = useCallback(async () => {
    if (busy) return;
    stopRequestedRef.current = false;
    if (workflowError?.kind === "completion_save_error") {
      setBusy(true);
      try {
        await saveWorkspaceState("completed", {
          scriptId: savedScriptId,
          completedAt: new Date().toISOString(),
          warnings: [],
        });
        if (!mountedRef.current) return;
        setWorkflowError(null);
        setCurrentMessage("项目地基已完成");
        toast.success("完成状态已保存");
        navigate(`/project/${project.id}/script-analysis`);
      } catch (stateError) {
        if (!mountedRef.current) return;
        setWorkflowError((previous) => ({
          ...(previous || {}),
          kind: "completion_save_error",
          stage: "completion",
          message: `所有 AI 拆分已完成，但完成状态仍保存失败：${stateError.message}`,
        }));
        toast.error("完成状态保存失败，未重复生成 AI 内容");
      } finally {
        if (mountedRef.current) setBusy(false);
      }
      return;
    }
    const failedStage = workflowError?.stage || activeStageRef.current || "script";
    if (!savedScriptId || failedStage === "script") {
      await startScriptBootstrap();
      return;
    }
    if (failedStage === "split") {
      if (splitPreview && splitEpisodes.length > 0) await confirmSplitAndContinue();
      else await startScriptBootstrap();
      return;
    }

    setBusy(true);
    setWorkflowError(null);
    try {
      const shouldResume = workflowError?.kind === "reconnecting" && Boolean(workflowError?.taskId);
      await runFoundationTasks(savedScriptId, {
        startAtStage: failedStage,
        resume: shouldResume ? {
          taskId: workflowError.taskId,
          internalTaskId: workflowError.internalTaskId,
          requestId: workflowError.requestId || workflowError.internalTaskId,
        } : {},
      });
    } catch (error) {
      if (isAbortError(error) || !mountedRef.current) return;
      // runOutlineTask 已经保存失败阶段、任务 ID 和错误详情；此处只保持页面可继续操作。
      toast.error(`${stageLabel(error.failedStage || failedStage)}失败: ${error.message}`);
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  }, [busy, confirmSplitAndContinue, navigate, project.id, runFoundationTasks, saveWorkspaceState, savedScriptId, splitEpisodes.length, splitPreview, startScriptBootstrap, toast, workflowError]);

  useEffect(() => {
    const recoveredStage = initialWorkspaceState?.currentStage;
    const taskId = initialWorkspaceState?.currentTaskId;
    if (
      autoResumeStartedRef.current
      || restoredStatus !== "in_progress"
      || !FOUNDATION_TASKS.some((task) => task.stageKey === recoveredStage)
      || !taskId
      || !savedScriptId
      || !scriptText.trim()
    ) return;

    autoResumeStartedRef.current = true;
    setBusy(true);
    void runFoundationTasks(savedScriptId, {
      startAtStage: recoveredStage,
      resume: {
        taskId,
        internalTaskId: initialWorkspaceState?.internalTaskId || "",
        requestId: initialWorkspaceState?.currentRequestId || initialWorkspaceState?.internalTaskId || "",
      },
    }).catch((error) => {
      if (isAbortError(error) || !mountedRef.current) return;
      toast.error(`${stageLabel(error.failedStage || recoveredStage)}恢复失败: ${error.message}`);
    }).finally(() => {
      if (mountedRef.current) setBusy(false);
    });
  }, [initialWorkspaceState, restoredStatus, runFoundationTasks, savedScriptId, scriptText, toast]);

  const skip = useCallback(async () => {
    await saveWorkspaceState("skipped", { skippedAt: new Date().toISOString() });
    toast.info("之后可以在剧本中心导入剧本，或从智能改编输入灵感开始。");
    onDone?.();
  }, [onDone, saveWorkspaceState, toast]);

  const goCreative = useCallback(async (target) => {
    await saveWorkspaceState("creative", { selectedAt: new Date().toISOString(), target });
    navigate(`/project/${project.id}/${target}`);
  }, [navigate, project.id, saveWorkspaceState]);

  const launchChoices = useMemo(() => ([
    {
      key: "script",
      icon: FileText,
      title: "导入剧本",
      desc: "已有完整剧本，导入后自动拆集、生成剧情大纲，并拆出角色、场景、道具。",
      compact: "已有完整剧本",
      detail: "最稳的起点。剧本会成为大小故事板、拆分镜、小故事板和视频生成的统一地基。",
      action: "导入剧本",
      onAction: (event) => transitionToMode("import", { immediate: event?.detail === 0 }),
    },
    {
      key: "creative",
      icon: Bot,
      title: "创意改编",
      desc: "从灵感、短篇、梗概或小说开始，先做智能改编，再进入分集生产。",
      compact: "从灵感开始",
      detail: "适合只有想法、小说片段或大纲的项目。先把内容改成可拍短剧，再进入剧本中心。",
      action: "选择入口",
      onAction: (event) => transitionToMode("creative", { immediate: event?.detail === 0 }),
    },
    {
      key: "dialogue",
      icon: Clapperboard,
      title: "对话短剧自动流",
      desc: "从已导入或已拆分的对话短剧剧本继续，进入集数确认、素材确认和分集生产链路。",
      compact: "正常短剧自动流",
      detail: "适合真人对话短剧。剧本需要先在剧本中心准备好；如果已经拆过集，会直接进入现有分集生产流程。",
      action: "进入自动流",
      onAction: () => goCreative("dialogue-auto"),
    },
    {
      key: "explainer",
      icon: Workflow,
      title: "一键解说剧",
      desc: "从小说原文开始，自动改写旁白剧、创建集数、生成资产、小故事板和小故事板视频。",
      compact: "小说原文自动流",
      detail: "适合批量生产旁白向短剧。系统默认复用已有资产、分镜和视频，只补缺口。",
      action: "进入一键生成",
      onAction: () => goCreative("one-click-explainer"),
    },
    {
      key: "skip",
      icon: Clock,
      title: "稍后再说",
      desc: "先进入现在的项目流程。后续可在剧本中心导入剧本，或在智能改编输入灵感开始。",
      compact: "先查看项目",
      detail: "不会创建任何内容，也不会触发模型调用。适合先查看项目结构。",
      action: "进入项目",
      onAction: skip,
    },
  ]), [goCreative, skip, transitionToMode]);

  return (
    <div className="h-full overflow-y-auto" style={{ backgroundColor: "var(--color-bg-primary)" }}>
      <div className="mx-auto min-h-full w-full max-w-6xl px-8 py-10">
        <main>
          <div className="mb-8">
            <div className="mb-3 inline-flex items-center gap-2 rounded-md border px-3 py-1 text-xs font-semibold"
              style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)", backgroundColor: "var(--color-bg-secondary)" }}>
              <Sparkles size={13} style={{ color: theme.colors.accentPrimary }} />
              新项目初始化
            </div>
            <h1 className="text-3xl font-bold tracking-normal" style={{ color: "var(--color-text-primary)" }}>
              从哪里开始这个项目？
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>
              先把项目地基搭好。导入剧本会自动拆分分集，并继续生成剧情大纲、角色、场景和道具。
            </p>
          </div>

          <div className={modeTransitionPhase === "exit" ? "ui-step-exit" : modeTransitionPhase === "enter" ? "ui-step-enter" : ""}>
          {mode === "choice" ? (
            <div className="space-y-4">
              <div className="grid gap-3 md:grid-cols-5">
                {launchChoices.map((item) => (
                  <ChoiceTab
                    key={item.key}
                    {...item}
                    selected={selectedChoice === item.key}
                    onSelect={() => setSelectedChoice(item.key)}
                    theme={theme}
                  />
                ))}
              </div>
              <ChoiceDetailPanel
                key={selectedChoice}
                choice={launchChoices.find((item) => item.key === selectedChoice) || launchChoices[0]}
                theme={theme}
              />
            </div>
          ) : mode === "creative" ? (
            <div className="rounded-md border p-5" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
              <div className="mb-5 flex items-center justify-between gap-4">
                <div>
                  <div className="text-lg font-bold" style={{ color: "var(--color-text-primary)" }}>选择改编入口</div>
                  <div className="mt-1 text-sm" style={{ color: "var(--color-text-muted)" }}>有灵感/梗概走智能改编，有小说原文走小说转剧本。</div>
                </div>
                <button type="button" onClick={(event) => transitionToMode("choice", { immediate: event.detail === 0 })} className="rounded-md px-3 py-2 text-sm transition-colors hover:bg-[var(--color-bg-tertiary)]" style={{ color: "var(--color-text-muted)" }}>
                  返回
                </button>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <button type="button" onClick={() => goCreative("adaptation-agent")} className="rounded-md border p-5 text-left transition-colors hover:bg-[var(--color-bg-tertiary)]"
                  style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-primary)" }}>
                  <Wand2 size={24} style={{ color: theme.colors.accentPrimary }} />
                  <div className="mt-4 font-bold">智能改编</div>
                  <div className="mt-2 text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>从一句灵感、短篇故事或项目设定开始。</div>
                </button>
                <button type="button" onClick={() => goCreative("novel-agent")} className="rounded-md border p-5 text-left transition-colors hover:bg-[var(--color-bg-tertiary)]"
                  style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-primary)" }}>
                  <BookOpen size={24} style={{ color: theme.colors.purplePrimary }} />
                  <div className="mt-4 font-bold">小说转剧本</div>
                  <div className="mt-2 text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>从小说原文出发，逐步转换成短剧项目。</div>
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-md border" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}>
              <div className="border-b p-5" style={{ borderColor: "var(--color-border-primary)" }}>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-lg font-bold" style={{ color: "var(--color-text-primary)" }}>导入剧本并初始化项目</div>
                    <div className="mt-1 text-sm" style={{ color: "var(--color-text-muted)" }}>支持 txt、md、docx，也可以直接粘贴。</div>
                  </div>
                  {!busy && (
                    <button type="button" onClick={(event) => transitionToMode("choice", { immediate: event.detail === 0 })} className="rounded-md px-3 py-2 text-sm transition-colors hover:bg-[var(--color-bg-tertiary)]" style={{ color: "var(--color-text-muted)" }}>
                      返回
                    </button>
                  )}
                </div>
              </div>
              <div className="grid gap-5 p-5 lg:grid-cols-[1fr_320px]">
                <div className="min-w-0">
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <input
                      value={scriptTitle}
                      onChange={(event) => setScriptTitle(event.target.value)}
                      disabled={busy}
                      className="h-9 min-w-[220px] rounded-md border px-3 text-sm outline-none"
                      style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)", color: "var(--color-text-primary)" }}
                    />
                    <button type="button" disabled={busy} onClick={() => fileInputRef.current?.click()}
                      className="h-9 rounded-md border px-3 text-sm font-semibold disabled:opacity-50"
                      style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-primary)", backgroundColor: "var(--color-bg-card)" }}>
                      <span className="inline-flex items-center gap-2"><Upload size={15} /> 选择文件</span>
                    </button>
                    <input ref={fileInputRef} type="file" accept=".txt,.md,.docx" className="hidden" onChange={handleFileChange} />
                  </div>
                  <textarea
                    value={scriptText}
                    disabled={busy}
                    onChange={handleScriptTextChange}
                    placeholder="粘贴总剧本内容..."
                    className="min-h-[380px] w-full resize-none rounded-md border p-4 text-sm leading-6 outline-none custom-scrollbar"
                    style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)", color: "var(--color-text-primary)" }}
                  />
                  {splitPreview && (
                    <SplitPreviewConfirm
                      preview={splitPreview}
                      busy={busy}
                      onConfirm={confirmSplitAndContinue}
                      onReset={() => clearSplitConfirmation("请重新拆分确认")}
                      theme={theme}
                    />
                  )}
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <label className="flex items-center gap-2 text-xs cursor-pointer select-none" style={{ color: "var(--color-text-secondary)" }} title="大纲对资产拆分非必需；勾选后只拆角色、场景、道具，速度更快">
                      <input
                        type="checkbox"
                        checked={skipOutline}
                        disabled={busy}
                        onChange={(e) => setSkipOutline(e.target.checked)}
                        className="accent-[var(--color-accent-primary)]"
                      />
                      跳过剧情大纲，只拆角色/场景/道具
                    </label>
                    <div className="flex items-center gap-3">
                      <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                        {scriptText.length > 0 ? `${scriptText.length.toLocaleString()} 字` : "等待剧本内容"}
                      </span>
                      <button
                        type="button"
                        disabled={!hasScriptText || busy}
                        onClick={startScriptBootstrap}
                        className="h-10 rounded-md px-5 text-sm font-bold text-[var(--color-accent-contrast)] disabled:cursor-not-allowed disabled:opacity-50"
                        style={{ backgroundColor: theme.colors.accentPrimary }}
                      >
                        {busy ? <span className="inline-flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> 处理中</span> : splitPreview ? "重新拆分" : "开始导入"}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <StagePanel stages={stages} currentMessage={currentMessage} theme={theme}
                    onStop={stopFoundationTask} stopping={stopping} />
                  {workflowError && (
                    <WorkflowIssueCard
                      key={`${workflowError.kind || "error"}:${workflowError.stage || "script"}`}
                      issue={workflowError}
                      busy={busy}
                      theme={theme}
                      onRetry={retryWorkflow}
                      onOpenScriptCenter={() => navigate(`/project/${project.id}/script-analysis`)}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
          </div>
        </main>
      </div>
    </div>
  );
};

const ChoiceTab = ({ icon, title, compact, onSelect, selected, theme }) => (
  <button
    type="button"
    role="button"
    onClick={onSelect}
    className={[
      "group flex min-h-[112px] w-full items-start gap-3 rounded-md border p-4 text-left outline-none",
      "transition-[transform,opacity,background-color,border-color,box-shadow] duration-200 ease-out",
      "focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg-primary)]",
      selected ? "opacity-100" : "opacity-[0.82] hover:opacity-100 hover:-translate-y-px",
    ].join(" ")}
    style={{
      borderColor: selected ? theme.colors.accentPrimary : "var(--color-border-primary)",
      backgroundColor: "var(--color-bg-secondary)",
      boxShadow: selected ? `0 0 0 1px ${theme.colors.accentPrimary}30` : "none",
      "--tw-ring-color": theme.colors.accentPrimary,
    }}
  >
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border"
      style={{
        borderColor: "var(--color-border-primary)",
        backgroundColor: "var(--color-bg-tertiary)",
        color: selected ? "var(--color-text-primary)" : "var(--color-text-muted)",
      }}>
      {React.createElement(icon, { size: 21 })}
    </div>
    <div className="min-w-0 flex-1">
      <div className="truncate text-base font-bold" style={{ color: "var(--color-text-primary)" }}>{title}</div>
      <div className="mt-1 text-sm leading-5" style={{ color: "var(--color-text-muted)" }}>{compact}</div>
      {selected && (
        <div className="mt-3 h-1 w-10 rounded-full" style={{ backgroundColor: theme.colors.accentPrimary }} />
      )}
    </div>
  </button>
);

const ChoiceDetailPanel = ({ choice, theme }) => {
  const Icon = choice.icon;
  return (
    <div
      key={choice.key}
      className="ui-step-enter rounded-md border p-6"
      style={{
        borderColor: theme.colors.accentPrimary,
        backgroundColor: "var(--color-bg-secondary)",
      }}
    >
      <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
        <div className="flex min-w-0 gap-5">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md border"
            style={{
              borderColor: "var(--color-border-primary)",
              backgroundColor: "var(--color-bg-tertiary)",
              color: "var(--color-text-secondary)",
            }}>
            <Icon size={28} />
          </div>
          <div className="min-w-0">
            <div className="text-2xl font-bold" style={{ color: "var(--color-text-primary)" }}>{choice.title}</div>
            <div className="mt-3 max-w-3xl text-sm leading-6" style={{ color: "var(--color-text-muted)" }}>{choice.desc}</div>
            <div className="mt-5 rounded-md border p-4 text-sm leading-6" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)", color: "var(--color-text-muted)" }}>
              {choice.detail}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={choice.onAction}
          className="inline-flex h-11 min-w-[144px] shrink-0 items-center justify-center gap-2 rounded-md px-5 text-sm font-bold text-[var(--color-accent-contrast)] transition-opacity duration-200 hover:opacity-90"
          style={{ backgroundColor: theme.colors.accentPrimary }}
        >
          {choice.action}
          <ArrowRight size={15} />
        </button>
      </div>
    </div>
  );
};
const SplitPreviewConfirm = ({ preview, busy, onConfirm, onReset, theme }) => {
  const episodes = Array.isArray(preview?.episodes) ? preview.episodes : [];
  const methodLabel = "规则拆分";
  return (
    <div className="mt-3 rounded-md border animate-in fade-in slide-in-from-bottom-2 duration-300" style={{ borderColor: `${theme.colors.accentPrimary}55`, backgroundColor: "var(--color-bg-primary)" }}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3" style={{ borderColor: "var(--color-border-primary)" }}>
        <div>
          <div className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>
            确认拆分结果
          </div>
          <div className="mt-1 text-xs" style={{ color: "var(--color-text-muted)" }}>
            {methodLabel}识别出 {episodes.length} 集。确认后才会写入分集，并继续生成剧情大纲、角色、场景和道具。
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onReset}
            className="h-8 rounded-md border px-3 text-xs font-semibold disabled:opacity-50"
            style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)", backgroundColor: "transparent" }}
          >
            取消确认
          </button>
          <button
            type="button"
            disabled={busy || episodes.length < 1}
            onClick={onConfirm}
            className="h-8 rounded-md px-3 text-xs font-bold text-[var(--color-accent-contrast)] disabled:opacity-50"
            style={{ backgroundColor: theme.colors.accentPrimary }}
          >
            {busy ? <span className="inline-flex items-center gap-1.5"><Loader2 size={13} className="animate-spin" /> 继续中</span> : "确认集数并继续"}
          </button>
        </div>
      </div>
      <div className="max-h-[220px] overflow-y-auto p-2 custom-scrollbar">
        {episodes.map((episode, index) => (
          <div key={`${episode.title || "episode"}-${index}`} className="rounded-md px-3 py-2" style={{ backgroundColor: index % 2 === 0 ? "var(--color-bg-secondary)" : "transparent" }}>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 truncate text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
                {episode.title || `第${index + 1}集`}
              </div>
              <div className="shrink-0 text-xs" style={{ color: "var(--color-text-muted)" }}>
                {(episode.wordCount || 0).toLocaleString()} 字
              </div>
            </div>
            {episode.preview && (
              <div className="mt-1 line-clamp-2 text-xs leading-5" style={{ color: "var(--color-text-muted)" }}>
                {episode.preview}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const WorkflowIssueCard = ({ issue, busy, theme, onRetry, onOpenScriptCenter }) => {
  const reconnecting = issue.kind === "reconnecting";
  const completionSaveError = issue.kind === "completion_save_error";
  const stateSaveWarning = issue.kind === "state_save_warning";
  const safetyStopped = issue.kind === "safety_stopped";
  const warning = reconnecting || completionSaveError || stateSaveWarning || safetyStopped;
  const color = warning ? "var(--color-warning)" : "var(--color-error)";
  return (
    <div className="ui-status-swap-enter rounded-md border p-4" role={warning ? "status" : "alert"} style={{ borderColor: color, backgroundColor: "var(--color-bg-primary)" }}>
      <div className="flex items-start gap-2">
        {reconnecting ? <WifiOff size={17} className="mt-0.5 shrink-0" style={{ color }} /> : <AlertTriangle size={17} className="mt-0.5 shrink-0" style={{ color }} />}
        <div className="min-w-0">
          <div className="text-sm font-bold" style={{ color }}>
            {reconnecting
              ? "正在恢复后台任务"
              : completionSaveError
                ? "AI 拆分已完成，状态保存失败"
                : stateSaveWarning
                  ? "任务仍在执行，状态同步失败"
                  : safetyStopped ? `${stageLabel(issue.stage)}已停止` : `${stageLabel(issue.stage)}失败`}
          </div>
          <div className="mt-1 whitespace-pre-wrap break-words text-xs leading-5" style={{ color: "var(--color-text-secondary)" }}>{issue.message}</div>
          {safetyStopped && <div className="mt-1 text-xs" style={{ color: "var(--color-text-muted)" }}>已保存结果保留；重新启动可能重新处理已完成分段，当前不支持分段断点续跑。</div>}
        </div>
      </div>
      {issue.taskId && (
        <div className="mt-2 break-all font-mono text-[10px]" style={{ color: "var(--color-text-muted)" }}>任务 ID：{issue.taskId}</div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" disabled={busy} onClick={onRetry} className="inline-flex h-8 items-center gap-1.5 rounded px-3 text-xs font-bold text-[var(--color-accent-contrast)] disabled:opacity-50" style={{ backgroundColor: theme.colors.accentPrimary }}>
          {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          {completionSaveError
            ? "重新保存完成状态"
            : reconnecting && issue.taskId
              ? "继续查询"
              : safetyStopped ? "重新启动本阶段" : "重试失败阶段"}
        </button>
        <button type="button" disabled={busy} onClick={onOpenScriptCenter} className="inline-flex h-8 items-center gap-1.5 rounded border px-3 text-xs font-semibold disabled:opacity-50" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)" }}>
          进入剧本中心
          <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
};

const StagePanel = ({ stages, currentMessage, theme, onStop, stopping = false }) => (
  <div className="rounded-md border p-4" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}>
    <div className="mb-4">
      <div className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>初始化进度</div>
      <div className="mt-1 min-h-[18px] text-xs" style={{ color: "var(--color-text-muted)" }}>{currentMessage || "等待开始"}</div>
    </div>
    <div className="space-y-3">
      {STAGE_DEFS.map((stage) => {
        const state = stages[stage.key] || {};
        const running = state.status === "running";
        const reconnecting = state.status === "reconnecting";
        const failed = state.status === "failed";
        const done = state.status === "done";
        const warning = state.status === "warning";
        const waiting = state.status === "waiting";
        const skipped = state.status === "skipped";
        const statusCategory = ["running", "done", "failed", "reconnecting"].includes(state.status)
          ? state.status
          : "other";
        const rawPercent = Number(state.percent);
        const hasPercent = Number.isFinite(rawPercent);
        const percent = hasPercent ? Math.max(0, Math.min(100, rawPercent)) : 0;
        const color = failed ? "var(--color-error)" : reconnecting || warning || waiting ? "var(--color-warning)" : done ? "var(--color-success)" : running ? theme.colors.accentPrimary : "var(--color-text-muted)";
        return (
          <div key={stage.key} className="flex gap-3" style={skipped ? { opacity: 0.55 } : undefined}>
            <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold"
              style={{
                backgroundColor: done ? "var(--color-success)" : failed ? "var(--color-error)" : (reconnecting || warning || waiting) ? "var(--color-warning)" : running ? theme.colors.accentPrimary : "var(--color-bg-tertiary)",
                color: done || failed || reconnecting || warning || waiting || running ? "#fff" : "var(--color-text-muted)",
              }}>
              <span key={statusCategory} className="ui-status-swap-enter inline-flex items-center justify-center">
                {done ? <Check size={13} /> : failed ? <AlertTriangle size={12} /> : reconnecting ? <WifiOff size={12} /> : running ? <Loader2 size={12} className="animate-spin" /> : waiting ? <Clock size={13} /> : skipped ? <Minus size={13} /> : warning ? "!" : ""}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold" style={{ color: failed ? "var(--color-error)" : "var(--color-text-primary)" }}>{stage.label}</div>
                {(running || reconnecting) && hasPercent && <span className="text-[10px] font-bold" style={{ color }}>{percent}%</span>}
              </div>
              <div key={`${statusCategory}:message`} className="ui-status-swap-enter text-xs leading-5" style={{ color: failed ? "var(--color-error)" : "var(--color-text-muted)" }}>{state.message || stage.desc}</div>
              {(running || reconnecting) && hasPercent && (
                <div className="mt-1 h-1 overflow-hidden rounded-full" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
                  <div className="h-full rounded-full" style={{ width: `${percent}%`, backgroundColor: color }} />
                </div>
              )}
              {Number(state.totalSegments) > 0 && (
                <div className="mt-1 text-[10px]" style={{ color: "var(--color-text-muted)" }}>已处理 {Number(state.completedSegments || 0)}/{Number(state.totalSegments)} 段</div>
              )}
              {state.reconnectMessage && <div className="mt-1 text-[10px]" style={{ color: "var(--color-warning)" }}>查询异常：{state.reconnectMessage}</div>}
              {state.attachmentMessage && <div className="mt-1 break-words text-[10px]" style={{ color: "var(--color-warning)" }}>{state.attachmentMessage}</div>}
              {state.taskId && <div className="mt-1 break-all font-mono text-[9px]" style={{ color: "var(--color-text-muted)" }}>任务 ID：{state.taskId}</div>}
              {(running || reconnecting) && state.taskId && FOUNDATION_TASKS.some((task) => task.stageKey === stage.key) && (
                <button type="button" disabled={stopping} onClick={onStop} className="mt-2 inline-flex h-8 items-center gap-1.5 rounded border px-3 text-xs font-semibold disabled:opacity-50" style={{ borderColor: "var(--color-border-primary)", color: "var(--color-warning)" }}>
                  {stopping && <Loader2 size={12} className="animate-spin" />}{stopping ? "正在停止" : "停止本批初始化"}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

export default ProjectOnboardingGate;
